import tenantTheme from '@theme';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../common';
import { formatWholeNumber } from '../../../utility/utility';
import { QUOTA_OTHERS_KEY, getQuotaSegmentColors, getQuotaTileClickProps } from '../../../utility/quota';
import { ProductGrid, ProductTile, StackBar } from './styled';

// Owners and admins see how much is still in the pool; agents and individuals how much they've used.
const TileMeta = ({ isAgencyView, notAssigned, used, children }) => {
  const { t } = useTranslation();
  return (
    <div className="tile-meta">
      {isAgencyView && <Icon icon="FiUsers" size="12px" color={tenantTheme['primary-color']} />}
      {children ||
        (isAgencyView
          ? t('{{count}} not assigned', { count: formatWholeNumber(notAssigned) })
          : t('{{count}} used so far', { count: formatWholeNumber(used) }))}
    </div>
  );
};

// One core product: what's available of its total. The breakdown is in the products drawer the tile opens.
const ProductTileCard = ({ product, isAgencyView, onOpen }) => {
  const { t } = useTranslation();
  return (
    <ProductTile
      style={{ '--tile-tint': `color-mix(in srgb, ${product.iconColor} 8%, ${tenantTheme['white-color']})` }}
      {...getQuotaTileClickProps(() => onOpen(product.slug), t(product.name))}
    >
      <div className="tile-head">
        <Icon icon={product.icon} size="20px" color={product.iconColor} />
        <div className="tile-name">
          <b title={t(product.name)}>{t(product.name)}</b>
        </div>
      </div>
      <div className="tile-amount">
        <b>{formatWholeNumber(product.available)}</b>
        <span>{t('available of {{total}}', { total: formatWholeNumber(product.total) })}</span>
      </div>
      <StackBar>
        <i style={{ flex: `${product.available} 1 0`, background: getQuotaSegmentColors(product.iconColor).main }} />
        <i style={{ flex: `${Math.max(product.total - product.available, 0)} 1 0` }} />
      </StackBar>
      <TileMeta isAgencyView={isAgencyView} notAssigned={product.notAssigned} used={product.used} />
    </ProductTile>
  );
};

// Add-ons (services, Golden Pin, Lead Marketplace) share one tile.
const OthersTile = ({ others, totals, isAgencyView, isAgent, onOpen }) => {
  const { t } = useTranslation();
  const held = others.filter((item) => item.total || item.available);
  const shown = isAgencyView ? others : held;
  return (
    <ProductTile {...getQuotaTileClickProps(() => onOpen(QUOTA_OTHERS_KEY), t('quota_others'))}>
      <div className="tile-head">
        <Icon icon="BsGrid" size="18px" color={tenantTheme['gray-dark-color']} />
        <div className="tile-name">
          <b>{t('quota_others')}</b>
        </div>
      </div>
      <div className="tile-amount">
        <b>{formatWholeNumber(totals.available)}</b>
        <span>{shown.length === 1 ? t(shown[0].name) : t('across {{count}} add-ons', { count: shown.length })}</span>
      </div>
      <StackBar>
        {others.map((item) => (
          <i key={item.slug} style={{ flex: `${item.available} 1 0`, background: item.iconColor }} />
        ))}
        {!totals.available && <i style={{ flex: '1 1 0' }} />}
      </StackBar>
      <TileMeta isAgencyView={isAgencyView} notAssigned={totals.notAssigned} used={totals.used}>
        {!isAgencyView && isAgent ? t('From your agency') : null}
      </TileMeta>
    </ProductTile>
  );
};

// One tile per core product, plus Others. `rail` (mobile): the tiles scroll sideways instead of wrapping.
const QuotaProductCards = ({ data, isAgencyView, isAgent, onOpen, rail }) => (
  <ProductGrid $rail={rail}>
    {(data?.core || []).map((product) => (
      <ProductTileCard key={product.slug} product={product} isAgencyView={isAgencyView} onOpen={onOpen} />
    ))}
    {!!data?.others?.length && (
      <OthersTile
        others={data.others}
        totals={data.othersTotals}
        isAgencyView={isAgencyView}
        isAgent={isAgent}
        onOpen={onOpen}
      />
    )}
  </ProductGrid>
);

export default QuotaProductCards;
